import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import { assessmentUpdateNotifier } from '../utils/assessmentUpdateNotifier';

interface AssessmentResponse {
  questionId: string;
  answer: number;
  timestamp: Date;
}

interface LegendarialData {
  responses: AssessmentResponse[];
  scores: {
    overall: number;
    category1: number; // SWLS
    category2: number; // PIL
  };
  results: {
    level: 'Low' | 'Moderate' | 'High';
    interpretation: string;
    recommendations: string[];
  };
}

const LegendarialAssessmentFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [responses, setResponses] = useState<AssessmentResponse[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculate total questions based on selected category
  const getTotalQuestions = () => {
    if (!selectedCategory) {
      return 1; // Just the category selection question
    }
    if (selectedCategory === 1) {
      return 5; // SWLS has 5 questions
    }
    if (selectedCategory === 2) {
      return 10; // PIL has 10 questions (short form)
    }
    return 5;
  };
  
  const totalQuestions = getTotalQuestions();

  // Category selection question
  const categorySelectionQuestion = {
    id: '0',
    question: 'Which Legendarial assessment would you like to take?',
    type: 'selection' as const,
    options: [
      'SWLS (Satisfaction with Life Scale)',
      'PIL (Purpose in Life)'
    ]
  };

  // Assessment questions organized by category
  const categoryQuestions = {
    1: [
      // SWLS (Satisfaction with Life Scale) - 5 questions
      {
        id: '1',
        question: 'In most ways my life is close to my ideal.',
        category: 1,
        type: 'likert'
      },
      {
        id: '2',
        question: 'The conditions of my life are excellent.',
        category: 1,
        type: 'likert'
      },
      {
        id: '3',
        question: 'I am satisfied with my life.',
        category: 1,
        type: 'likert'
      },
      {
        id: '4',
        question: 'So far I have gotten the important things I want in life.',
        category: 1,
        type: 'likert'
      },
      {
        id: '5',
        question: 'If I could live my life over, I would change almost nothing.',
        category: 1,
        type: 'likert'
      }
    ],
    2: [
      // PIL (Purpose in Life) - 10 questions (short form for SOWBEE)
      {
        id: '1',
        question: 'I have a clear sense of meaning in my life.',
        category: 2,
        type: 'likert'
      },
      {
        id: '2',
        question: 'I am aware of what makes my life meaningful.',
        category: 2,
        type: 'likert'
      },
      {
        id: '3',
        question: 'I have discovered satisfying goals and a clear direction for my life.',
        category: 2,
        type: 'likert'
      },
      {
        id: '4',
        question: 'I feel my life has lasting value.',
        category: 2,
        type: 'likert'
      },
      {
        id: '5',
        question: 'My personal existence is full of purpose.',
        category: 2,
        type: 'likert'
      },
      {
        id: '6',
        question: 'I can clearly state my life\'s mission or calling.',
        category: 2,
        type: 'likert'
      },
      {
        id: '7',
        question: 'I am living the kind of life I want to live.',
        category: 2,
        type: 'likert'
      },
      {
        id: '8',
        question: 'I believe I am making a lasting contribution to others.',
        category: 2,
        type: 'likert'
      },
      {
        id: '9',
        question: 'My daily activities are connected to something bigger than myself.',
        category: 2,
        type: 'likert'
      },
      {
        id: '10',
        question: 'I feel my life story will leave a legacy.',
        category: 2,
        type: 'likert'
      }
    ]
  };

  // Get current questions based on selected category
  const getCurrentQuestions = () => {
    if (!selectedCategory) {
      return [categorySelectionQuestion];
    }
    // When category is selected, only return category-specific questions
    return categoryQuestions[selectedCategory as keyof typeof categoryQuestions] || [];
  };

  const questions = getCurrentQuestions();

  // Get answer options based on selected category
  const getAnswerOptions = () => {
    if (selectedCategory === 1) {
      // SWLS uses 1-7 scale
      return [
        { value: 1, label: 'Strongly Disagree' },
        { value: 2, label: 'Disagree' },
        { value: 3, label: 'Slightly Disagree' },
        { value: 4, label: 'Neither Agree nor Disagree' },
        { value: 5, label: 'Slightly Agree' },
        { value: 6, label: 'Agree' },
        { value: 7, label: 'Strongly Agree' }
      ];
    } else if (selectedCategory === 2) {
      // PIL uses 1-7 scale
      return [
        { value: 1, label: 'Strongly Disagree' },
        { value: 2, label: 'Disagree' },
        { value: 3, label: 'Slightly Disagree' },
        { value: 4, label: 'Neither Agree nor Disagree' },
        { value: 5, label: 'Slightly Agree' },
        { value: 6, label: 'Agree' },
        { value: 7, label: 'Strongly Agree' }
      ];
    } else {
      return [];
    }
  };

  const answerOptions = getAnswerOptions();

  const currentQuestionData = questions[currentQuestion - 1];
  const currentResponse = currentQuestionData ? responses.find(r => r.questionId === currentQuestionData.id) : null;
  const progress = (currentQuestion / totalQuestions) * 100;

  // Type guard to check if question has options (selection type)
  const isSelectionQuestion = (question: any): question is { id: string; question: string; type: 'selection'; options: string[] } => {
    return question.type === 'selection' && 'options' in question;
  };

  // Type guard to check if question has category (likert type)
  const isLikertQuestion = (question: any): question is { id: string; question: string; category: number; type: string } => {
    return question.type === 'likert' && 'category' in question;
  };

  const getCategoryName = (categoryNumber: number) => {
    switch (categoryNumber) {
      case 1: return 'SWLS (Satisfaction with Life Scale)';
      case 2: return 'PIL (Purpose in Life)';
      default: return 'Unknown Category';
    }
  };

  const saveCurrentResponse = (answer: number) => {
    if (!currentQuestionData) return;
    
    const newResponse: AssessmentResponse = {
      questionId: currentQuestionData.id,
      answer,
      timestamp: new Date()
    };

    setResponses(prev => {
      const filtered = prev.filter(r => r.questionId !== currentQuestionData.id);
      return [...filtered, newResponse];
    });

    // If this is the category selection question, set the selected category
    if (currentQuestionData.id === '0') {
      const categoryMap = [1, 2]; // Map selection index to category number
      setSelectedCategory(categoryMap[answer]);
      // Reset to question 1 for the selected category
      setCurrentQuestion(1);
    }
  };

  const handleNext = () => {
    if (currentQuestion < totalQuestions) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentQuestion > 1) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const calculateScores = (responses: AssessmentResponse[], selectedCategory: number) => {
    // Filter out the category selection response (id: '0')
    const assessmentResponses = responses.filter(r => r.questionId !== '0');
    
    if (selectedCategory === 1) {
      // SWLS scoring (sum of all 5 questions, max 35)
      const swlsResponses = assessmentResponses.filter(r => 
        categoryQuestions[1]?.some(q => q.id === r.questionId)
      );
      
      const swlsTotal = swlsResponses.reduce((sum, r) => sum + r.answer, 0);
      // Use raw total for interpretation (5-35 scale)

      return {
        overall: swlsTotal,
        category1: swlsTotal,
        category2: 0,
        swlsTotal: swlsTotal // Store raw total for interpretation
      };
    } else if (selectedCategory === 2) {
      // PIL scoring (sum of all 10 questions, max 70)
      const pilResponses = assessmentResponses.filter(r => 
        categoryQuestions[2]?.some(q => q.id === r.questionId)
      );
      
      const pilTotal = pilResponses.reduce((sum, r) => sum + r.answer, 0);
      // Use raw total for interpretation (10-70 scale)

      return {
        overall: pilTotal,
        category1: 0,
        category2: pilTotal,
        pilTotal: pilTotal // Store raw total for interpretation
      };
    } else {
      return {
        overall: 0,
        category1: 0,
        category2: 0
      };
    }
  };

  const interpretResults = (scores: any, selectedCategory: number) => {
    if (selectedCategory === 1) {
      // SWLS interpretation based on official scoring ranges
      const { swlsTotal } = scores;
      
      let level: 'Low' | 'Moderate' | 'High';
      let interpretation: string;
      let recommendations: string[] = [];
      
      if (swlsTotal >= 31) {
        level = 'High';
        interpretation = 'Extremely satisfied with life. You report being extremely satisfied with your life overall. This suggests you feel your life is very close to your ideal and you have achieved the important things you want.';
        recommendations = [
          'Continue maintaining your current positive outlook and life strategies',
          'Consider sharing your approaches to life satisfaction with others',
          'Stay connected with the activities and relationships that bring you fulfillment',
          'Keep setting new meaningful goals to maintain engagement'
        ];
      } else if (swlsTotal >= 26) {
        level = 'High';
        interpretation = 'Satisfied with life. You report being satisfied with your life overall. This suggests you feel your life is close to your ideal and you have achieved most of the important things you want.';
        recommendations = [
          'Continue maintaining your current positive outlook and life strategies',
          'Consider sharing your approaches to life satisfaction with others',
          'Stay connected with the activities and relationships that bring you fulfillment',
          'Keep setting new meaningful goals to maintain engagement'
        ];
      } else if (swlsTotal >= 21) {
        level = 'Moderate';
        interpretation = 'Slightly satisfied with life. You experience a moderate level of satisfaction with your life. There may be some areas where you feel content and others where you see room for improvement.';
        recommendations = [
          'Reflect on what aspects of your life bring you the most satisfaction',
          'Identify areas where you might want to make changes or improvements',
          'Practice gratitude to enhance your appreciation for positive aspects',
          'Set specific goals for areas you want to develop further'
        ];
      } else if (swlsTotal >= 20) {
        level = 'Moderate';
        interpretation = 'Neutral about life satisfaction. You have a balanced view of your life satisfaction, neither particularly satisfied nor dissatisfied. This suggests room for growth and development.';
        recommendations = [
          'Reflect on what would make your life more meaningful and satisfying',
          'Identify specific areas where you want to see improvement',
          'Consider taking the Purpose in Life (PIL) assessment for additional insights',
          'Set achievable goals that align with your values and interests'
        ];
      } else if (swlsTotal >= 15) {
        level = 'Low';
        interpretation = 'Slightly dissatisfied with life. You may be experiencing some dissatisfaction with your current life circumstances. This suggests there are areas where you feel your life could be better.';
        recommendations = [
          'Reflect on what changes you might want to make in your life',
          'Focus on small, achievable goals that could improve your daily satisfaction',
          'Consider speaking with a counselor or life coach to explore your concerns',
          'Practice gratitude and mindfulness to enhance positive experiences'
        ];
      } else if (swlsTotal >= 10) {
        level = 'Low';
        interpretation = 'Dissatisfied with life. You are experiencing significant dissatisfaction with your current life circumstances. This indicates a need for reflection and potentially making changes to improve your overall life satisfaction.';
        recommendations = [
          'Consider speaking with a counselor or life coach to explore your concerns',
          'Focus on small, achievable goals that could improve your daily satisfaction',
          'Consider taking the Purpose in Life (PIL) assessment for additional insights',
          'Explore activities or relationships that have brought you joy in the past',
          'Seek support from friends, family, or mental health professionals'
        ];
      } else {
        level = 'Low';
        interpretation = 'Extremely dissatisfied with life. You are experiencing very high levels of dissatisfaction with your life circumstances. This suggests a strong need for support and potentially significant life changes.';
        recommendations = [
          'Seek immediate support from mental health professionals or counselors',
          'Consider reaching out to crisis support resources if needed',
          'Focus on basic self-care and reaching out to trusted support people',
          'Consider taking the Purpose in Life (PIL) assessment for additional insights',
          'Explore activities or relationships that have brought you joy in the past'
        ];
      }
      
      return { level, interpretation, recommendations };
    } else if (selectedCategory === 2) {
      // PIL interpretation based on official scoring ranges (10-70 scale)
      const { pilTotal } = scores;
      
      let level: 'Low' | 'Moderate' | 'High';
      let interpretation: string;
      let recommendations: string[] = [];
      
      if (pilTotal >= 50) {
        level = 'High';
        interpretation = 'High sense of purpose. You have a strong sense of meaning and purpose in your life. You feel that your life has clear direction, meaningful goals, and lasting significance. This suggests strong existential well-being and life satisfaction.';
        recommendations = [
          'Continue pursuing your current goals and meaningful activities',
          'Consider mentoring others who may be seeking purpose',
          'Stay connected with the values and activities that give your life meaning',
          'Keep setting new meaningful challenges and goals',
          'Share your sense of purpose with others to inspire them'
        ];
      } else if (pilTotal >= 30) {
        level = 'Moderate';
        interpretation = 'Moderate sense of purpose. You have some sense of direction and meaning in your life, but there may be areas where you feel uncertain or unclear about your purpose. This is common and manageable with reflection and action.';
        recommendations = [
          'Reflect on what activities and relationships give your life the most meaning',
          'Consider exploring new interests or activities that align with your values',
          'Set specific goals that feel personally meaningful to you',
          'Connect with others who share similar values or interests',
          'Explore how your daily activities connect to larger life goals'
        ];
      } else {
        level = 'Low';
        interpretation = 'Low sense of purpose. You may be experiencing a lack of direction or meaning in your life. This can lead to feelings of emptiness, confusion, or lack of motivation. It may be helpful to explore what gives your life meaning and purpose.';
        recommendations = [
          'Consider speaking with a counselor or life coach to explore your values and goals',
          'Take time to reflect on what activities or relationships have brought you fulfillment in the past',
          'Experiment with new activities, hobbies, or volunteer opportunities',
          'Consider taking the Satisfaction with Life Scale (SWLS) for additional insights',
          'Focus on building meaningful relationships and connections with others',
          'Explore your personal values and how you might live more aligned with them',
          'Consider volunteering or contributing to causes you care about'
        ];
      }
      
      return { level, interpretation, recommendations };
    } else {
      return {
        level: 'Low' as const,
        interpretation: 'Unable to interpret results.',
        recommendations: ['Please complete the assessment again.']
      };
    }
  };

  const handleSubmit = async () => {
    // Filter out the category selection response for validation
    const assessmentResponses = responses.filter(r => r.questionId !== '0');
    if (assessmentResponses.length < totalQuestions) {
      alert('Please answer all questions before submitting.');
      return;
    }

    setIsSubmitting(true);

    try {
      const scores = calculateScores(responses, selectedCategory!);
      const results = interpretResults(scores, selectedCategory!);

      const assessmentData: LegendarialData = {
        responses,
        scores,
        results
      };

      const response = await fetch(API_CONFIG.ENDPOINTS.LEGENDARIAL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include',
        body: JSON.stringify(assessmentData)
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('SESSION_EXPIRED');
        }
        throw new Error('Failed to submit assessment');
      }

      const result = await response.json();
      
      // Clear Insights page cache to force refresh
      const userId = JSON.parse(localStorage.getItem('user') || '{}').id;
      const insightsCacheKey = userId ? `lifepsensor_insights_cache_${userId}` : 'lifepsensor_insights_cache';
      localStorage.removeItem(insightsCacheKey);
      
      // Notify InsightsPage about the update
      assessmentUpdateNotifier.notifyUpdate('legendarial');
      
      // Navigate to completion page
      navigate('/panel/learn/legendarial-assessment/completion', {
        state: { assessmentResult: result.data }
      });

    } catch (error) {

      let errorMessage = 'Error submitting assessment. Please try again.';
      if (error instanceof Error && error.message === 'SESSION_EXPIRED') {
        errorMessage = 'Your session has expired. Please log in again to continue.';
      }
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitting) {
    return (
      <div className="assessment-form-page">
        <div className="assessment-container">
          <div className="assessment-left-panel">
            <div className="assessment-header">
              <h1>Legendarial Assessment</h1>
              <p>Processing your responses...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if currentQuestionData exists
  if (!currentQuestionData) {
    return (
      <div className="assessment-form-page">
        <div className="assessment-container">
          <div className="assessment-left-panel">
            <div className="assessment-header">
              <h1>Legendarial Assessment</h1>
              <p>Loading assessment questions...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-form-page">
      <div className="assessment-container">
        {/* Left Panel - Background Image */}
        <div
          className="assessment-left-panel legendarial-panel"
        >
          <div className="assessment-header">
            <h1>Legendarial Assessment</h1>
            <p className="assessment-subtitle">
              Discovering your life satisfaction and purpose
            </p>
            <p className="assessment-tool-info">
              Comprehensive assessment of life satisfaction and meaning in life
            </p>
          </div>
        </div>

        {/* Right Panel - Questions */}
        <div className="assessment-right-panel">
          <div className="assessment-progress">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
            <span className="progress-text">
              Question {currentQuestion} of {totalQuestions}
            </span>
          </div>

          <div className="question-section">
            {isSelectionQuestion(currentQuestionData) ? (
              <>
                <h2 className="question-title">
                  {currentQuestionData.question}
                </h2>
                <p className="question-instruction">
                  Please select the assessment category you would like to take.
                </p>

                <div className="answer-options selection-options">
                  {currentQuestionData.options.map((option: string, index: number) => (
                    <button
                      key={index}
                      className={`option-button selection-button ${currentResponse?.answer === index ? 'selected' : ''}`}
                      onClick={() => {
                        saveCurrentResponse(index);
                      }}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </>
            ) : isLikertQuestion(currentQuestionData) ? (
              <>
                <div className="category-indicator">
                  <span className="category-badge">
                    {getCategoryName(currentQuestionData.category)}
                  </span>
                </div>
                
                <h2 className="question-title">
                  {currentQuestionData.question}
                </h2>
                <p className="question-instruction">
                  Please rate how well this statement describes you using the scale below.
                </p>

                <div className="answer-options">
                  {answerOptions.map((option) => (
                    <label key={option.value} className="answer-option">
                      <input
                        type="radio"
                        name={`question-${currentQuestion}`}
                        value={option.value}
                        checked={currentResponse?.answer === option.value}
                        onChange={(e) => saveCurrentResponse(parseInt(e.target.value))}
                      />
                      <span className="option-text">{option.label}</span>
                    </label>
                  ))}
                </div>
              </>
            ) : null}
          </div>

          {selectedCategory !== null && (
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={() => {
                  if (currentQuestion === 1 && selectedCategory === null) {
                    navigate('/panel/learn/');
                    return;
                  }
                  if (currentQuestion === 1 && selectedCategory !== null) {
                    setSelectedCategory(null);
                    setCurrentQuestion(1);
                    return;
                  }
                  handleBack();
                }}
                disabled={false}
              >
                Back
              </button>

              <button
                className="btn-primary"
                onClick={handleNext}
                disabled={!currentResponse}
              >
                {currentQuestion === totalQuestions ? 'Complete Assessment' : 'Next'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LegendarialAssessmentFormPage;


